import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, FileText, DollarSign, Users, Filter } from 'lucide-react';
import AccessLogTable from './AccessLogTable';

interface AdminDashboardProps {
  onLogout: () => void;
  userEmail: string;
}

interface PitchDeckLog {
  id: string;
  email: string;
  accessed_at: string;
  campaign_code: string | null;
}

interface CampaignAccessLog {
  id: string;
  email: string;
  accessed_at: string;
  campaign_code: string;
}

interface Campaign {
  campaign_code: string;
  campaign_name: string;
}

const AdminDashboard = ({ onLogout, userEmail }: AdminDashboardProps) => {
  const [pitchDeckLogs, setPitchDeckLogs] = useState<PitchDeckLog[]>([]);
  const [campaignAccessLogs, setCampaignAccessLogs] = useState<CampaignAccessLog[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch campaigns for filter dropdown
      const { data: campaignsData } = await supabase
        .from('investment_campaigns')
        .select('campaign_code, campaign_name');
      
      if (campaignsData) {
        setCampaigns(campaignsData);
      }

      // Fetch pitch deck logs
      const { data: pitchLogs, error: pitchError } = await supabase
        .from('pitch_deck_access_logs')
        .select('id, email, accessed_at, campaign_code')
        .order('accessed_at', { ascending: false });

      if (pitchError) {
        console.error('Error fetching pitch deck logs:', pitchError);
      } else {
        setPitchDeckLogs(pitchLogs || []);
      }

      // Fetch campaign access logs
      const { data: accessLogs, error: accessError } = await supabase
        .from('campaign_access_logs')
        .select('id, email, accessed_at, campaign_code')
        .order('accessed_at', { ascending: false });

      if (accessError) {
        console.error('Error fetching campaign access logs:', accessError);
      } else {
        setCampaignAccessLogs(accessLogs || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCampaignName = (code: string | null) => {
    if (!code) return 'Unknown';
    const campaign = campaigns.find(c => c.campaign_code === code);
    return campaign?.campaign_name || code;
  };

  const filteredPitchLogs = selectedChapter === 'all' 
    ? pitchDeckLogs 
    : pitchDeckLogs.filter(log => log.campaign_code === selectedChapter);

  const filteredAccessLogs = selectedChapter === 'all'
    ? campaignAccessLogs
    : campaignAccessLogs.filter(log => log.campaign_code === selectedChapter);

  // Calculate stats
  const totalPitchViews = pitchDeckLogs.length;
  const totalInvestmentUnlocks = campaignAccessLogs.length;
  const uniqueEmails = new Set([
    ...pitchDeckLogs.map(l => l.email),
    ...campaignAccessLogs.map(l => l.email)
  ]).size;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Logged in as {userEmail}</p>
          </div>
          <Button variant="outline" onClick={onLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pitch Deck Views</CardTitle>
              <FileText className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalPitchViews}</div>
              <p className="text-xs text-muted-foreground">Total access attempts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Investment Unlocks</CardTitle>
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalInvestmentUnlocks}</div>
              <p className="text-xs text-muted-foreground">Investment offer views</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{uniqueEmails}</div>
              <p className="text-xs text-muted-foreground">Distinct email addresses</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filter by Chapter:</span>
          <Select value={selectedChapter} onValueChange={setSelectedChapter}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="All Chapters" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Chapters</SelectItem>
              {campaigns.map(campaign => (
                <SelectItem key={campaign.campaign_code} value={campaign.campaign_code}>
                  {campaign.campaign_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pitch-deck" className="space-y-4">
          <TabsList>
            <TabsTrigger value="pitch-deck">
              Pitch Deck Access ({filteredPitchLogs.length})
            </TabsTrigger>
            <TabsTrigger value="investment">
              Investment Access ({filteredAccessLogs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pitch-deck">
            <Card>
              <CardHeader>
                <CardTitle>Pitch Deck Access Logs</CardTitle>
              </CardHeader>
              <CardContent>
                <AccessLogTable 
                  logs={filteredPitchLogs.map(log => ({
                    id: log.id,
                    email: log.email,
                    chapter: getCampaignName(log.campaign_code),
                    timestamp: log.accessed_at,
                  }))}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="investment">
            <Card>
              <CardHeader>
                <CardTitle>Investment Offer Access Logs</CardTitle>
              </CardHeader>
              <CardContent>
                <AccessLogTable 
                  logs={filteredAccessLogs.map(log => ({
                    id: log.id,
                    email: log.email,
                    chapter: getCampaignName(log.campaign_code),
                    timestamp: log.accessed_at,
                  }))}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
